import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavParams, PopoverController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import Markdown from 'markdown-it';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { STATUS_COLUMN_FORMAT_MAP } from 'src/app/constants';
import {
  FilterOption,
  FilterValue,
  FILTER_OPERATORS,
  reconstructAndHydrateUiFilters,
} from 'src/app/utils/filter-utils';
import { generateCustomFieldsArray, genericCompareFn } from 'src/app/utils/utils';
import { FetchDataRequest, FetchDataResponse, createDataSource } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { environment } from 'src/environments/environment';
import {
  CUSTOM_FIELD_TYPES,
  SIMPLE_CONDITIONS_OPERATORS,
  SubStatus,
  CustomField,
  Conversation,
  CustomFieldMetadata,
  SHOW_IN_POST_TICKET_MESSAGE_VALUES,
} from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AIAutofillService } from 'src/services/ai-autofill.service';
import { ApiService } from 'src/services/api.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { AvatarModule } from '../avatar/avatar.module';
import { BranchingEditorComponent } from '../branching-editor/branching-editor.component';
import { BranchingConfig, Branch } from '../branching-editor/types';
import { ChannelSelectorModule } from '../channel-selector/channel-selector.module';
import { CustomFieldsModule } from '../custom-fields/custom-fields.module';
import { ResponseInputModule } from '../custom-fields/response-input/response-input.module';
import { FilterButtonComponent } from '../filter-button/filter-button.component';
import { TiptapEditorComponent } from '../tiptap-editor/tiptap-editor.component';

type SubStatusWithIcon = SubStatus & { icon?: string; iconClass?: string };

interface ConversationWithField {
  id: string;
  friendlyId: string;
  title: string;
  ticketFieldFeedback: boolean | null;
  ticketFieldValue: string;
  prefillAnalysis?: string;
  isSupportTeamMemberAnalysis?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    NgxTippyModule,
    AvatarModule,
    ResponseInputModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    RouterModule,
    SharedPipesModule,
    TiptapEditorComponent,
    BranchingEditorComponent,
    FilterButtonComponent,
  ],
  selector: 'app-edit-custom-field',
  templateUrl: './edit-custom-field.component.html',
  styleUrls: ['./edit-custom-field.component.scss'],
})
export class EditCustomFieldComponent implements OnInit {
  isDev: boolean;
  isLoading = false;
  id?: string;
  projectId: string;
  field: CustomField = {
    label: '',
    description: '',
    type: CUSTOM_FIELD_TYPES.SHORT_ANSWER,
    required: false,
    public: true,
    id: uuidv4(),
    conditions: {
      type: 'simple',
      simple: {},
    },
    conditionalOptions: {
      branches: [],
    },
    prefillOnSubStatusId: null,
    prefillPrompt: null,
    requiredOnSubStatusId: null,
    showInTriageMessages: true,
    showInPostTicketMessage: SHOW_IN_POST_TICKET_MESSAGE_VALUES.INITIAL_MESSAGE,
  };

  CUSTOM_FIELD_TYPE_MULTI_SELECT = CUSTOM_FIELD_TYPES.MULTI_SELECT;
  CUSTOM_FIELD_TYPE_SINGLE_SELECT = CUSTOM_FIELD_TYPES.SINGLE_SELECT;
  SHOW_IN_POST_TICKET_MESSAGE_VALUES = SHOW_IN_POST_TICKET_MESSAGE_VALUES;

  branchingConfig: BranchingConfig;
  filterOptions: FilterOption[] = [];
  conditionalOptionsMode = false;

  // Analysis editing states
  editingRowId: string | null = null;
  editingAnalysis: string = '';
  isSavingAnalysis = false;

  availableSubStatuses: SubStatusWithIcon[] = [];
  selectedSubStatusDisplay: SubStatusWithIcon | null = null;
  selectedRequiredSubStatusDisplay: SubStatusWithIcon | null = null;

  conversationsWithField: Partial<Conversation>[] = [];
  conversationsDataSource;
  isConversationsLoading = false;
  isShowingConversations = false;

  constructor(
    navParams: NavParams,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private apiService: ApiService,
    private customFieldsService: CustomFieldsService,
    private searchablePopoverService: SearchablePopoverService,
    private popoverCtrl: PopoverController,
    private aiAutofillService: AIAutofillService,
    private projectsService: ProjectsService,
  ) {
    this.id = navParams.get('id');
    this.projectId = navParams.get('projectId');
    this.isDev = environment.environment === 'dev';
  }

  async ngOnInit() {
    await this.loadSubStatuses();
    this.initializeFilterOptions();
    if (this.id) {
      await this.loadCustomField();
      await this.initializeConversationsTable();
    } else {
      await this.initializeBranchingConfig();
    }
    this.initializeConditionalOptionsToggle();
  }

  async toggleConversations() {
    this.isShowingConversations = !this.isShowingConversations;
    if (this.isShowingConversations) {
      if (!this.conversationsDataSource) {
        await this.initializeConversationsTable();
      }
      // Load data after Angular renders the table (next tick)
      setTimeout(() => this.loadConversations(), 0);
    }
    this.initializeFilterOptions();
    this.initializeConditionalOptionsToggle();
  }

  async initializeBranchingConfig() {
    const branches: Branch[] = await Promise.all(
      (this.field.conditionalOptions?.branches || []).map(async (branch) => {
        const filters = new Map();

        if (branch.conditions?.simple) {
          try {
            // Transform conditions to the format expected by reconstructAndHydrateUiFilters
            const savedFilters = this.transformConditionsToSavedFormat(branch.conditions.simple);

            // Get filter lookups for proper label resolution
            const filterLookups = await this.getFilterLookups();

            // Get properly labeled filters
            const hydratedFilters = await reconstructAndHydrateUiFilters(
              savedFilters,
              filterLookups,
              this.filterOptions,
              this.apiService,
              this.customFieldsService,
            );

            // Convert the hydrated result back to Map format for branching editor
            hydratedFilters.forEach((values, option) => {
              filters.set(option, values);
            });
          } catch (error) {
            console.error('Error hydrating conditional options filters:', error);
            this.msgSrvc.showError('Failed to load conditional options filters.');
          }
        }

        return {
          ...branch,
          id: uuidv4(),
          steps: branch.options || [],
          filters,
          content: {},
          options: branch.options || [],
          conditions: branch.conditions,
        };
      }),
    );

    // If no branches exist, create default structure with two branches
    if (branches.length === 0) {
      // First branch: conditional branch with empty filter (user will set this)
      branches.push({
        id: uuidv4(),
        filters: new Map(),
        steps: [],
        content: {},
        options: [],
        conditions: undefined,
      });

      // Second branch: fallback branch with original field options
      // This makes it clear what options will be shown when no conditions match
      branches.push({
        id: uuidv4(),
        filters: new Map(),
        steps: [...(this.field.options || [])],
        content: {},
        options: [...(this.field.options || [])],
        conditions: undefined,
      });
    }

    this.branchingConfig = {
      branches,
      allowEmptyConditions: true,
      minBranches: 1,
      maxBranches: 10,
    };
  }

  initializeFilterOptions() {
    this.filterOptions = [
      {
        id: 'ticketTypeId',
        icon: 'ticket',
        searchId: 'ticketTypeId',
        label: 'Ticket Type',
        type: 'select',
        searchablePopoverOptions: {
          projectId: this.projectId,
        },
      } as FilterOption,
      {
        label: 'Priority',
        id: 'priority',
        searchId: 'priority',
        icon: 'alert-circle',
        type: 'select',
      },
    ];
  }

  async loadCustomFieldsForFiltering() {
    if (this.filterOptions.some((opt) => opt.id.startsWith('ticketTypeFields'))) {
      return; // Already loaded
    }

    try {
      // 1. Load project fields FIRST using projectsService.getById
      const project = await this.projectsService.getById(this.projectId);
      const projectFields = project.fields || [];

      // 2. Load custom fields for this project
      const allowedTypes: (keyof typeof CUSTOM_FIELD_TYPES)[] = [
        'CHECKBOX',
        'SINGLE_SELECT',
        'MULTI_SELECT',
        'DATE',
        'USER_SELECT',
        'MULTI_USER_SELECT',
        'USER_GROUP_SELECT',
        'MULTI_USER_GROUP_SELECT',
      ];

      const allowedTypeValues = allowedTypes.map((key) => CUSTOM_FIELD_TYPES[key]);

      const customFieldsResponse = await this.customFieldsService.list({
        projectId: this.projectId,
        limit: null,
      });

      // Filter and combine fields
      const combinedFields: any[] = [];

      // Add project fields first (they are already hydrated from backend)
      for (const projectField of projectFields) {
        // Project fields are hydrated and should have type, label, etc.
        // We need to type guard since GlobalCustomField might not have type
        if ('type' in projectField && projectField.type && allowedTypeValues.includes(projectField.type as any)) {
          combinedFields.push({
            ...projectField,
            isProjectLevel: true,
          });
        }
      }

      // Add custom fields that aren't already in project fields
      const projectFieldIds = new Set(projectFields.map((pf) => pf.id));
      for (const customField of customFieldsResponse.data) {
        if (!projectFieldIds.has(customField.id) && allowedTypeValues.includes(customField.type)) {
          combinedFields.push({
            ...customField,
            isProjectLevel: false,
          });
        }
      }

      // Instead of creating individual filter options, we add a single "Custom Field" option
      // that will use the existing custom fields searchable popover
      const customFieldOption: FilterOption = {
        id: 'customFieldId',
        icon: 'flask',
        label: 'Custom Field',
        type: 'variable' as const,
        searchablePopoverOptions: {
          allowedTypes: allowedTypeValues,
          projectId: this.projectId,
        },
      };

      // Add to existing filter options if not already present
      if (!this.filterOptions.some((opt) => opt.id === 'customFieldId')) {
        this.filterOptions.push(customFieldOption);
      }
    } catch (error) {
      console.error('Error loading custom fields for filtering:', error);
    }
  }

  initializeConditionalOptionsToggle() {
    // Default to false - only enable if there are meaningful conditional options
    this.conditionalOptionsMode = false;

    // Only enable if conditional options exist with actual branches that have content
    if (this.field.conditionalOptions?.branches?.length > 0) {
      // Check if there are branches with actual conditions or options configured
      const hasMeaningfulBranches = this.field.conditionalOptions.branches.some((branch) => {
        // A branch is meaningful if it has conditions OR options configured
        return (
          (branch.conditions?.simple && Object.keys(branch.conditions?.simple).length > 0) ||
          (branch.options && Array.isArray(branch.options) && branch.options.length > 0)
        );
      });

      this.conditionalOptionsMode = hasMeaningfulBranches;
    }
  }

  async onConditionalOptionsModeChange() {
    if (!this.conditionalOptionsMode) {
      // When disabling, clear conditional options
      this.field.conditionalOptions = null;
      this.branchingConfig = {
        branches: [],
        minBranches: 1,
        maxBranches: 10,
      };
    } else {
      // When enabling, load custom fields for filtering and initialize structure
      await this.loadCustomFieldsForFiltering();

      if (!this.field.conditionalOptions) {
        this.field.conditionalOptions = {
          branches: [],
        };
      }
      await this.initializeBranchingConfig();
    }
  }

  onBranchingConfigChange(config: BranchingConfig) {
    this.field.conditionalOptions = {
      branches: config.branches.map((branch) => {
        let conditions: any = undefined;

        if (branch.filters && branch.filters.size > 0) {
          // Convert filters to SimpleConditions format
          const conditionLeaves: any[] = [];

          branch.filters.forEach((value: FilterValue[], key: FilterOption) => {
            // For custom fields, we need to get the actual selected values, not the field IDs
            let filterValues: any[];
            if (key.id === 'customFieldId' || key.id.startsWith('ticketTypeFields.')) {
              // For custom fields, use the .values property which contains the actual selected values
              filterValues = value.map((filterValue: FilterValue) => filterValue.values);
            } else {
              // For standard fields, use the .id property
              filterValues = value.map((filterValue: FilterValue) => filterValue.id);
            }

            // Determine the correct leftValueFrom format
            let leftValueFrom: string;
            if (key.id === 'customFieldId' || key.id.startsWith('ticketTypeFields.')) {
              // For custom fields, extract the field ID and use bracket notation
              let customFieldId: string;
              if (key.id === 'customFieldId') {
                // This shouldn't happen with the new implementation, but handle it just in case
                customFieldId = value[0]?.id as string;
              } else {
                // Extract field ID from ticketTypeFields.fieldId format
                customFieldId = key.id.replace('ticketTypeFields.', '');
              }
              leftValueFrom = `conversation.ticketTypeFields['${customFieldId}']`;
            } else {
              // For standard fields, use dot notation
              leftValueFrom = `conversation.${key.id}`;
            }

            if (filterValues.length === 1) {
              // Single value - use equals operator
              conditionLeaves.push({
                leftValueFrom,
                operator:
                  key.operator === FILTER_OPERATORS.NOT_EQUALS
                    ? SIMPLE_CONDITIONS_OPERATORS.NOT_EQUALS
                    : SIMPLE_CONDITIONS_OPERATORS.EQUALS,
                rightValue: filterValues[0],
              });
            } else if (filterValues.length > 1) {
              // Multiple values - use in operator
              conditionLeaves.push({
                leftValueFrom,
                operator:
                  key.operator === FILTER_OPERATORS.NOT_EQUALS
                    ? SIMPLE_CONDITIONS_OPERATORS.NOT_IN
                    : SIMPLE_CONDITIONS_OPERATORS.IN,
                rightValue: filterValues,
              });
            }
          });

          if (conditionLeaves.length === 1) {
            // Single condition - use it directly
            conditions = conditionLeaves[0];
          } else if (conditionLeaves.length > 1) {
            // Multiple conditions - wrap in 'and'
            conditions = {
              and: conditionLeaves,
            };
          }
        }

        return {
          conditions: {
            simple: conditions,
          },
          options: branch.steps || [],
        };
      }),
    };

    this.field = { ...this.field };
  }

  async initializeConversationsTable() {
    if (!this.id) return;
    this.conversationsDataSource = createDataSource(this.fetchConversationsWithField);
  }

  // Helper function to find metadata for a specific custom field
  private getFieldMetadata(conversation: Conversation, fieldId: string): CustomFieldMetadata | null {
    if (!conversation.ticketFieldMetadata || !Array.isArray(conversation.ticketFieldMetadata)) {
      return null;
    }
    return conversation.ticketFieldMetadata.find((metadata) => metadata.customFieldId === fieldId) || null;
  }

  fetchConversationsWithField = async ({
    limit = 10,
    cursor,
    order = ['createdAt'],
    descending = true,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<Conversation>>> => {
    if (!this.id) {
      return { data: [], cursors: { next: undefined }, totalCount: 0 };
    }
    this.isConversationsLoading = true;
    try {
      const response = (await this.customFieldsService.getConversationsWithField(this.id, {
        limit,
        cursor,
        order,
        descending,
      })) as {
        data: Conversation[];
        cursors: { next?: string; previous?: string };
        totalCount: number;
      };

      const currentPageData: ConversationWithField[] = (response.data || []).map((conv: Conversation) => {
        const fieldMetadata = this.getFieldMetadata(conv, this.id);
        // Format array values for display (JSONB can contain arrays for multi-select fields)
        const ticketFieldValue = Array.isArray(fieldMetadata?.suggestedValue)
          ? fieldMetadata.suggestedValue.join(', ')
          : fieldMetadata?.suggestedValue;
        return {
          id: conv.id,
          friendlyId: conv.friendlyId,
          title: conv.title,
          ticketFieldFeedback: fieldMetadata?.approvedPrefill,
          ticketFieldValue,
          prefillAnalysis: fieldMetadata?.prefillAnalysis,
          isSupportTeamMemberAnalysis: fieldMetadata?.isSupportTeamMemberAnalysis,
          createdAt: conv.createdAt,
          updatedAt: conv.updatedAt,
        };
      });

      return {
        data: currentPageData,
        cursors: response.cursors,
        totalCount: response.totalCount,
      };
    } catch (error) {
      console.error('Error fetching conversations with field:', error);
      this.msgSrvc.showError('Failed to load conversations using this field.');
      if (!cursor) {
        this.conversationsWithField = [];
      }
      return { data: [], cursors: { next: undefined }, totalCount: 0 };
    } finally {
      this.isConversationsLoading = false;
    }
  };

  async loadConversations() {
    if (this.conversationsDataSource) {
      await this.conversationsDataSource.firstPage();
    }
  }

  async loadSubStatuses() {
    if (!this.projectId) {
      console.warn('Project ID not available, cannot load sub-statuses.');
      return;
    }
    try {
      const result = await firstValueFrom(
        this.apiService.post<{ data: SubStatus[] }>('/sub-statuses/list', {
          where: [{ field: 'projectId', operator: '==', value: this.projectId }],
          order: [
            ['typeOrd', 'ASC'],
            ['position', 'ASC'],
          ],
          limit: 1000,
        }),
      );
      this.availableSubStatuses = (result.data || []).map((subStatus) => {
        const type = subStatus.key.split('__')[0];
        const format = STATUS_COLUMN_FORMAT_MAP[type];
        return {
          ...subStatus,
          icon: format?.icon,
          iconClass: format?.iconClass,
        };
      });
    } catch (error) {
      console.error('Error loading sub-statuses:', error);
      this.msgSrvc.showError('Failed to load status options.');
      this.availableSubStatuses = [];
    }
    this.updateSelectedSubStatusDisplay();
  }

  async loadCustomField() {
    try {
      const customField = await this.customFieldsService.getById(this.id);
      this.field = customField;
      this.projectId = customField.projectId;
      await this.initializeBranchingConfig();
      this.initializeConditionalOptionsToggle();
      this.updateSelectedSubStatusDisplay();
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  updateSelectedSubStatusDisplay() {
    if (this.field.prefillOnSubStatusId) {
      this.selectedSubStatusDisplay =
        this.availableSubStatuses.find((s) => s.id === this.field.prefillOnSubStatusId) || null;
    } else {
      this.selectedSubStatusDisplay = null;
    }

    if (this.field.requiredOnSubStatusId) {
      this.selectedRequiredSubStatusDisplay =
        this.availableSubStatuses.find((s) => s.id === this.field.requiredOnSubStatusId) || null;
    } else {
      this.selectedRequiredSubStatusDisplay = null;
    }
  }

  showSubStatusPopover(event: Event) {
    const popoverItems = [
      { id: null, title: 'None', icon: undefined, iconClass: undefined, object: null },
      ...this.availableSubStatuses.map((subStatus) => ({
        id: subStatus.id,
        title: `${subStatus.name} (${subStatus.type})`,
        icon: subStatus.icon,
        iconClass: subStatus.iconClass,
        object: subStatus,
      })),
    ];

    this.searchablePopoverService.showPopover({
      event,
      selectedItemId: this.field.prefillOnSubStatusId ?? undefined,
      showSearch: false,
      componentView: 'SubStatusSelect',
      initialItems: popoverItems,
      callback: (id, _name, object) => {
        this.field.prefillOnSubStatusId = id;
        this.selectedSubStatusDisplay = object;
      },
      loadData: async () => {
        return popoverItems;
      },
    });
  }

  showRequiredSubStatusPopover(event: Event) {
    // Filter out intake (draft) and the first open status since fields can't be required at initial stages
    const validSubStatuses = this.availableSubStatuses.filter((subStatus) => subStatus.type !== 'draft');

    const popoverItems = [
      { id: null, title: 'None', icon: undefined, iconClass: undefined, object: null },
      ...validSubStatuses.map((subStatus) => ({
        id: subStatus.id,
        title: `${subStatus.name} (${subStatus.type})`,
        icon: subStatus.icon,
        iconClass: subStatus.iconClass,
        object: subStatus,
      })),
    ];

    this.searchablePopoverService.showPopover({
      event,
      selectedItemId: this.field.requiredOnSubStatusId ?? undefined,
      showSearch: false,
      componentView: 'SubStatusSelect',
      initialItems: popoverItems,
      callback: (id, _name, object) => {
        this.field.requiredOnSubStatusId = id;
        this.selectedRequiredSubStatusDisplay = object;
      },
      loadData: async () => {
        return popoverItems;
      },
    });
  }

  dismiss(data?: any) {
    this.modalCtrl.dismiss(data);
  }

  async submit() {
    this.isLoading = true;

    // Only update conditional options from branching config if conditional options mode is enabled
    if (this.conditionalOptionsMode && this.branchingConfig?.branches?.length > 0) {
      this.onBranchingConfigChange(this.branchingConfig);
    }

    const canSetAllowedValues = [
      CUSTOM_FIELD_TYPES.USER_SELECT,
      CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
      CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
      CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
    ].includes(this.field.type as any);

    const canSetOptions = [CUSTOM_FIELD_TYPES.SINGLE_SELECT, CUSTOM_FIELD_TYPES.MULTI_SELECT].includes(
      this.field.type as any,
    );

    const shouldSetConditionalOptions = canSetOptions && !this.field.externalSelect && this.conditionalOptionsMode;

    const cleanedField = {
      ...this.field,
      label: this.field.label?.trim() || `New ${this.field.type} field`,
      options: canSetOptions && !this.field.externalSelect ? this.field.options : null,
      allowedValues: canSetAllowedValues ? this.field.allowedValues : null,
      conditionalOptions: shouldSetConditionalOptions ? this.field.conditionalOptions : null,
      projectId: this.projectId,
      prefillOnSubStatusId: this.field.prefillOnSubStatusId,
      prefillPrompt: this.field.prefillPrompt,
      requiredOnSubStatusId: this.field.requiredOnSubStatusId,
      showInTriageMessages: this.field.showInTriageMessages,
      showInPostTicketMessage: this.field.showInPostTicketMessage,
    };

    try {
      if (this.id) {
        const result = await this.customFieldsService.updatePartial(this.id, cleanedField);
        this.field = result;
        await this.msgSrvc.show('Custom field updated!');
        this.dismiss({ customField: result });
      } else {
        const result = await this.customFieldsService.create(cleanedField);
        await this.msgSrvc.show('Custom field added!');
        this.dismiss({ customField: result });
      }
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete custom field?',
      subHeader: `Please remove this field from any ticket types before deleting. Deletion can't be undone.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.customFieldsService.delete(this.id);
              this.isLoading = false;
              this.dismiss({ customField: this.field });
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async duplicate() {
    const alert = await this.alertCtrl.create({
      header: 'Duplicate custom field?',
      subHeader: `You can confirm details before you save.`,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Duplicate',
          handler: () => {
            this.field.id = undefined;
            this.id = undefined;
            setTimeout(() => {
              this.field.label = `Copy of ${this.field.label}`;
            }, 500);
          },
        },
      ],
    });
    alert.present();
  }

  getPreviewField() {
    return generateCustomFieldsArray([this.field], [], {});
  }

  private async approveAIPrefill(row: ConversationWithField): Promise<void> {
    if (!row.id) {
      console.error('Missing conversationId for approving prefill.');
      return;
    }
    try {
      await this.aiAutofillService.approvePrefill({ conversationId: row.id, fieldId: this.id });
      await this.conversationsDataSource.reloadPage();
    } catch (error) {
      console.error('Error approving prefill for field:', this.id, error);
    }
  }

  private async rejectAIPrefill(row: ConversationWithField): Promise<void> {
    if (!row.id) {
      console.error('Missing conversationId for rejecting prefill.');
      return;
    }
    try {
      await this.aiAutofillService.rejectPrefill({ conversationId: row.id, fieldId: this.id });
      await this.conversationsDataSource.reloadPage();
    } catch (error) {
      console.error('Error rejecting prefill for field:', this.id, error);
    }
  }

  async showPrefillOption(row: ConversationWithField) {
    const LABEL_APPROVE_PREFILL = 'Approve Prefill';
    const LABEL_REJECT_PREFILL = 'Reject Prefill';

    const options: ActionDropdownType[] = [
      {
        label: LABEL_APPROVE_PREFILL,
        icon: 'thumbs-up-outline',
      },
      {
        label: LABEL_REJECT_PREFILL,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL_APPROVE_PREFILL) {
            await this.approveAIPrefill(row);
          } else if (label === LABEL_REJECT_PREFILL) {
            await this.rejectAIPrefill(row);
          }
        },
      },
    });
    popover.present();
  }

  renderMarkdown(content: string): string {
    if (!content) return '';

    const md = new Markdown({
      breaks: true,
      linkify: true,
      html: true,
    });

    return md.render(content);
  }

  startEditingAnalysis(row: ConversationWithField): void {
    this.editingRowId = row.id;
    this.editingAnalysis = row.prefillAnalysis || '';
  }

  cancelEditingAnalysis(): void {
    this.editingRowId = null;
    this.editingAnalysis = '';
  }

  async saveAnalysis(row: ConversationWithField): Promise<void> {
    if (!this.id || !row.id) {
      console.error('Missing required IDs for saving analysis');
      return;
    }

    try {
      this.isSavingAnalysis = true;
      await this.aiAutofillService.updatePrefillAnalysis({
        conversationId: row.id,
        fieldId: this.id,
        analysis: this.editingAnalysis,
      });

      // Reset editing state
      this.editingRowId = null;
      this.editingAnalysis = '';

      // Reload the data to show updated analysis
      await this.conversationsDataSource.reloadPage();
      this.msgSrvc.show('Analysis updated successfully');
    } catch (error) {
      console.error('Error saving analysis:', error);
      this.msgSrvc.showError('Failed to save analysis');
    } finally {
      this.isSavingAnalysis = false;
    }
  }

  onConditionalOptionsChange() {
    this.onBranchingConfigChange(this.branchingConfig);
  }

  removeOption(branch: Branch, index: number) {
    if (branch.steps) {
      branch.steps.splice(index, 1);
      this.onBranchingConfigChange(this.branchingConfig);
    }
  }

  onConditionalOptionsReorder({ detail }, branch: Branch) {
    const { from, to } = detail;

    if (branch.steps) {
      branch.steps.splice(to, 0, branch.steps.splice(from, 1)[0]);
      this.onBranchingConfigChange(this.branchingConfig);
    }

    detail.complete();
  }

  trackByIndex(index: number, _item: any) {
    return index;
  }

  /**
   * Transform conditional options format to the format expected by reconstructAndHydrateUiFilters
   */
  transformConditionsToSavedFormat(conditions: any): { key: string; operator: any; values: string[] }[] {
    const result: { key: string; operator: any; values: string[] }[] = [];

    // Handle different condition structures
    const conditionLeaves: any[] = [];

    if (conditions.and) {
      // Multiple conditions wrapped in 'and'
      conditionLeaves.push(...conditions.and);
    } else if (conditions.or) {
      // Multiple conditions wrapped in 'or'
      conditionLeaves.push(...conditions.or);
    } else {
      // Single condition
      conditionLeaves.push(conditions);
    }

    // Convert each condition leaf to saved filter format
    conditionLeaves.forEach((leaf) => {
      if (leaf.leftValueFrom) {
        let fieldName: string;

        // Handle custom field conditions specially
        const customFieldMatch = leaf.leftValueFrom.match(/^conversation\.ticketTypeFields\['(.+?)'\]$/);
        if (customFieldMatch) {
          // Convert conversation.ticketTypeFields['fieldId'] to ticketTypeFields.fieldId format
          fieldName = `ticketTypeFields.${customFieldMatch[1]}`;
        } else {
          // Extract the field name from leftValueFrom (e.g., "conversation.ticketTypeId" -> "ticketTypeId")
          fieldName = leaf.leftValueFrom.replace('conversation.', '');
        }

        // Handle null operators that don't have rightValue
        if (
          leaf.operator === SIMPLE_CONDITIONS_OPERATORS.IS_NULL ||
          leaf.operator === SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL
        ) {
          result.push({
            key: fieldName,
            operator: leaf.operator,
            values: ['null'], // Use 'null' as a placeholder value for UI hydration
          });
        } else {
          // Convert values to array format for other operators
          let values: string[];
          if (leaf.operator === SIMPLE_CONDITIONS_OPERATORS.IN && Array.isArray(leaf.rightValue)) {
            values = leaf.rightValue.map(String);
          } else {
            values = [String(leaf.rightValue)];
          }

          result.push({
            key: fieldName,
            operator: leaf.operator,
            values: values,
          });
        }
      }
    });

    return result;
  }

  /**
   * Collect all lookup IDs from conditional options branches
   */
  collectLookupIdsFromConditionalOptions(): Record<string, string[]> {
    const lookupIds: Record<string, string[]> = {};

    if (!this.field.conditionalOptions?.branches) {
      return lookupIds;
    }

    for (const branch of this.field.conditionalOptions.branches) {
      if (!branch.conditions?.simple) {
        continue;
      }

      // Transform conditions to saved filter format to extract lookup IDs
      const savedFilters = this.transformConditionsToSavedFormat(branch.conditions.simple);

      for (const { key, values } of savedFilters) {
        // Handle custom field conditions specially
        if (key.startsWith('ticketTypeFields.')) {
          // For custom fields, we need to collect the field ID for customFieldId lookup
          const fieldId = key.replace('ticketTypeFields.', '');
          if (!lookupIds['customFieldId']) {
            lookupIds['customFieldId'] = [];
          }
          if (!lookupIds['customFieldId'].includes(fieldId)) {
            lookupIds['customFieldId'].push(fieldId);
          }

          // Also collect the values for the specific field type lookup
          // This will be handled by reconstructAndHydrateUiFilters based on field type
        } else {
          // Handle standard field lookups
          if (!lookupIds[key]) {
            lookupIds[key] = [];
          }

          // Add unique values to the lookup collection
          for (const value of values) {
            if (!lookupIds[key].includes(value)) {
              lookupIds[key].push(value);
            }
          }
        }
      }
    }

    return lookupIds;
  }

  /**
   * Fetch filter lookups using the existing saved-filters/get-lookup-values endpoint
   */
  async getFilterLookups(): Promise<Record<string, Record<string, string>>> {
    // Collect all lookup IDs from conditional options
    const lookupIds = this.collectLookupIdsFromConditionalOptions();

    // If no values to lookup, return empty
    if (Object.keys(lookupIds).length === 0) {
      return {};
    }

    try {
      // Use the existing endpoint that handles all lookup types
      const response = (await this.apiService.postPromise('/saved-filters/get-lookup-values', lookupIds)) as Record<
        string,
        Record<string, string>
      >;
      return response || {};
    } catch (error) {
      console.error('Error fetching filter lookups:', error);
      return {};
    }
  }

  showInPostTicketMessageCompareFn(v1, v2) {
    return genericCompareFn(v1, v2);
  }
}
